import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useGetSurveyByIdQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service.js";
import SectionProgressBar from "./SectionProgressBar.jsx";
import {
  PRME_ORGANIZATION_ID,
  Roles,
  SurveyActions,
  SurveyStatus,
} from "@/shared/utils/constants";
import { canUserPerformActionOnSurvey } from "@/features/survey/permissions.ts";
import SectionContent from "./SectionContent.jsx";
import BaseButton from "@/components_v2/Button/BaseButton.js";
import { ArrowLeftOutlined } from "@ant-design/icons";
import PreviewIcon from "@/assets/icons/preview.svg?react";
import SendIcon from "@/assets/icons/send.svg?react";
import SubmissionConfirmationModal from "./SubmissionConfirmationModal.jsx";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer.js";
import { SurveyType } from "@/shared/utils/constants";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip.js";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.js";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants.js";

const SurveySectionForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const location = useLocation();
  const isSample = location.pathname.includes("sample");

  const [hasPermissions, setHasPermissions] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const { data: survey, isLoading: isSurveyLoading } = useGetSurveyByIdQuery({
    surveyId,
    filters: { organization: isSample ? PRME_ORGANIZATION_ID : null },
  });
  const [
    getSurveyStatistics,
    { data: surveyStatistics, isLoading: isSurveyStatisticsLoading },
  ] = useLazyGetSurveyStatisticsQuery();
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    if (survey) {
      if (
        survey.status === SurveyStatus.ENDED.value ||
        survey.details?.isSubmitted
      ) {
        navigate(`/reports/${survey.id}/report`);
      } else {
        getSurveyStatistics({
          survey: survey.id,
          organization: isSample ? PRME_ORGANIZATION_ID : null,
        });
      }
    }

    if (currentUser && survey) {
      if (currentUser.role === Roles.ADMIN.value || survey.details == null) {
        navigate("/");
        return;
      }

      const hasPermissions = canUserPerformActionOnSurvey(
        survey.permissions,
        currentUser.organizationRole,
      );
      if (!hasPermissions) {
        navigate("/");
      }
      setHasPermissions(hasPermissions);
    }
  }, [survey, currentUser]);

  const canCurrentUserSubmit = useMemo(() => {
    if (!survey || !currentUser) return;
    return canUserPerformActionOnSurvey(
      survey.permissions,
      currentUser.organizationRole,
      SurveyActions.SUBMIT.value,
    );
  }, [survey, currentUser]);

  if (
    isSurveyLoading ||
    isSurveyStatisticsLoading ||
    !currentUser ||
    !hasPermissions
  ) {
    return <BaseSpinner size="large" />;
  }

  return survey ? (
    <div className="survey-section-form-container">
      <div className="survey-section-form-header">
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.LINK}
          leadingIcon={<ArrowLeftOutlined />}
          to={`/reports/${surveyId}${isSample ? "/sample" : ""}`}
          type={BUTTON_TYPES.LINK}
        >
          {t("buttons.backToSurveyOverview", {
            surveyType: SurveyType.getLabel(survey.type),
          })}
        </BaseButton>

        {!isSample && (
          <div className="survey-section-form-header-right-container">
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              leadingIcon={
                <BaseSvgRenderer
                  width="20"
                  height="20"
                  component={PreviewIcon}
                />
              }
              to={`/reports/${survey.id}/report`}
              type={BUTTON_TYPES.LINK}
            >
              {t("previewSurvey")}
            </BaseButton>
            <BaseTooltip
              title={
                canCurrentUserSubmit
                  ? null
                  : t("survey.labels.onlyAdminsCanSubmit")
              }
            >
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                disabled={!canCurrentUserSubmit}
                leadingIcon={
                  <BaseSvgRenderer
                    width="20"
                    height="20"
                    component={SendIcon}
                  />
                }
                onClick={() => setOpenConfirmationModal(true)}
              >
                {t("submitSurvey")}
              </BaseButton>
            </BaseTooltip>
          </div>
        )}
      </div>

      <div className="survey-section-form-content-container">
        {surveyStatistics && <SectionProgressBar sections={surveyStatistics} />}

        <SectionContent activeSurveyId={survey.id} />
      </div>

      {!isSample && (
        <SubmissionConfirmationModal
          onCancel={() => setOpenConfirmationModal(false)}
          survey={survey}
          open={openConfirmationModal}
        />
      )}
    </div>
  ) : (
    <div className="survey-section-form-container-empty">
      <h1 className="header-m-medium">{t("noActiveSurvey")}</h1>
    </div>
  );
};

export default SurveySectionForm;
